import React, { useEffect, useRef, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  TouchSensor,
  KeyboardSensor,
  closestCorners,
  useSensor,
  useSensors,
  useDroppable,
  DragStartEvent,
  DragOverEvent,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

export interface BoardColumnData<C = any> {
  id: string;
  title: string;
  cards: C[];
}

interface KanbanBoardProps<C = any> {
  columns: BoardColumnData<C>[];
  getCardId: (card: C) => string;
  renderCard: (card: C) => React.ReactNode;
  onCardMove: (cardId: string, toColumnId: string, toIndex: number) => void;
  renderColumnHeader?: (col: BoardColumnData<C>) => React.ReactNode;
  renderColumnFooter?: (col: BoardColumnData<C>) => React.ReactNode;
}

function SortableCard({ id, children }: { id: string; children: React.ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    touchAction: 'none',
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </div>
  );
}

function Column<C>({
  col,
  getCardId,
  renderCard,
  renderColumnHeader,
  renderColumnFooter,
}: {
  col: BoardColumnData<C>;
  getCardId: (card: C) => string;
  renderCard: (card: C) => React.ReactNode;
  renderColumnHeader?: (col: BoardColumnData<C>) => React.ReactNode;
  renderColumnFooter?: (col: BoardColumnData<C>) => React.ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: col.id });
  const cardIds = col.cards.map(getCardId);
  return (
    <div className="flex w-[280px] flex-shrink-0 flex-col">
      <div className="mb-2 flex items-center justify-between px-1">
        {renderColumnHeader ? (
          renderColumnHeader(col)
        ) : (
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">{col.title}</span>
            <span className="rounded-full bg-slate-200 px-2 py-0.5 text-xs text-slate-600 dark:bg-gray-700 dark:text-slate-300">
              {col.cards.length}
            </span>
          </div>
        )}
      </div>
      <div
        ref={setNodeRef}
        className={`flex min-h-[120px] flex-1 flex-col gap-2 rounded-2xl p-2 transition-colors ${
          isOver ? 'bg-slate-200/70 dark:bg-gray-700/50' : 'bg-slate-100/70 dark:bg-gray-800/40'
        }`}
      >
        <SortableContext items={cardIds} strategy={verticalListSortingStrategy}>
          {col.cards.map((card) => (
            <SortableCard key={getCardId(card)} id={getCardId(card)}>
              {renderCard(card)}
            </SortableCard>
          ))}
        </SortableContext>
        {col.cards.length === 0 && (
          <div className="flex flex-1 items-center justify-center rounded-xl border border-dashed border-slate-300 py-6 text-xs text-slate-400 dark:border-gray-700">
            Trascina qui
          </div>
        )}
        {renderColumnFooter && <div className="mt-1">{renderColumnFooter(col)}</div>}
      </div>
    </div>
  );
}

export function KanbanBoard<C>({
  columns,
  getCardId,
  renderCard,
  onCardMove,
  renderColumnHeader,
  renderColumnFooter,
}: KanbanBoardProps<C>) {
  const [board, setBoard] = useState<BoardColumnData<C>[]>(columns);
  const [activeId, setActiveId] = useState<string | null>(null);
  const draggingRef = useRef(false);

  // Ri-sincronizza dai props quando non c'è un drag in corso (evita di sovrascrivere
  // il riordino ottimistico mentre l'utente trascina).
  useEffect(() => {
    if (!draggingRef.current) setBoard(columns);
  }, [columns]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const findColumnId = (itemId: string): string | null => {
    if (board.some((c) => c.id === itemId)) return itemId;
    const col = board.find((c) => c.cards.some((k) => getCardId(k) === itemId));
    return col ? col.id : null;
  };

  const handleDragStart = (e: DragStartEvent) => {
    draggingRef.current = true;
    setActiveId(String(e.active.id));
  };

  const handleDragOver = (e: DragOverEvent) => {
    const { active, over } = e;
    if (!over) return;
    const activeColId = findColumnId(String(active.id));
    const overColId = findColumnId(String(over.id));
    if (!activeColId || !overColId || activeColId === overColId) return;

    setBoard((prev) => {
      const activeCol = prev.find((c) => c.id === activeColId);
      const overCol = prev.find((c) => c.id === overColId);
      if (!activeCol || !overCol) return prev;
      const card = activeCol.cards.find((k) => getCardId(k) === String(active.id));
      if (!card) return prev;
      let overIdx = overCol.cards.findIndex((k) => getCardId(k) === String(over.id));
      if (overIdx === -1) overIdx = overCol.cards.length;
      return prev.map((c) => {
        if (c.id === activeColId) return { ...c, cards: c.cards.filter((k) => getCardId(k) !== String(active.id)) };
        if (c.id === overColId) {
          const cards = [...c.cards];
          cards.splice(overIdx, 0, card);
          return { ...c, cards };
        }
        return c;
      });
    });
  };

  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    draggingRef.current = false;
    setActiveId(null);
    if (!over) {
      setBoard(columns);
      return;
    }
    const overColId = findColumnId(String(over.id));
    if (!overColId) {
      setBoard(columns);
      return;
    }
    const finalCol = board.find((c) => c.id === overColId);
    if (!finalCol) return;
    const oldIndex = finalCol.cards.findIndex((k) => getCardId(k) === String(active.id));
    let newIndex = finalCol.cards.findIndex((k) => getCardId(k) === String(over.id));
    if (newIndex === -1) newIndex = finalCol.cards.length - 1;
    let nextBoard = board;
    if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
      nextBoard = board.map((c) =>
        c.id === overColId ? { ...c, cards: arrayMove(c.cards, oldIndex, newIndex) } : c
      );
      setBoard(nextBoard);
    }
    const targetCol = nextBoard.find((c) => c.id === overColId)!;
    const targetIndex = targetCol.cards.findIndex((k) => getCardId(k) === String(active.id));
    onCardMove(String(active.id), overColId, Math.max(0, targetIndex));
  };

  const activeCard = activeId
    ? board.flatMap((c) => c.cards).find((k) => getCardId(k) === activeId) || null
    : null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={() => {
        draggingRef.current = false;
        setActiveId(null);
        setBoard(columns);
      }}
    >
      <div className="flex gap-4 overflow-x-auto pb-4">
        {board.map((col) => (
          <Column
            key={col.id}
            col={col}
            getCardId={getCardId}
            renderCard={renderCard}
            renderColumnHeader={renderColumnHeader}
            renderColumnFooter={renderColumnFooter}
          />
        ))}
      </div>
      <DragOverlay>{activeCard ? <div className="rotate-1">{renderCard(activeCard)}</div> : null}</DragOverlay>
    </DndContext>
  );
}

export default KanbanBoard;
